import { useDispatch, useSelector } from 'react-redux';

import { useErrorToast } from '~/common/errors/useErrorToast';
import { api } from '~/redux/api/createApi';
import {
  clearVendorConnecting,
  selectIsVendorConnecting,
  setVendorConnecting,
} from '~/redux/slices/eSignature';

import { ESignatureIntegration, IntegrationMap } from '../types';

export function useESignatureIntegrationActions(
  params: UseESignatureIntegrationActionsParams,
) {
  const { draft } = params;
  const { toast } = useErrorToast();
  const dispatch = useDispatch();
  const [getAuthenticate, { isLoading: isAuthenticateLoading }] =
    api.endpoints.getAuthenticate.useLazyQuery();
  const [createEsignatureIntegration, { isLoading: isCreateLoading }] =
    api.endpoints.createEsignatureIntegration.useMutation();

  const isVendorConnecting = useSelector(
    selectIsVendorConnecting(draft?.providerName),
  );

  const executeReauthenticate = async () => {
    try {
      const response = await getAuthenticate({
        return_to: window.location.href,
      }).unwrap();

      if (response?.authenticateUrl) {
        window.location.href = response.authenticateUrl;
      }
    } catch (error) {
      if (error instanceof Error) {
        toast(error, { autoDismiss: false });
      }
      dispatch(clearVendorConnecting());
    }
  };

  const executeCreateIntegration = async () => {
    if (!draft) return;

    try {
      if (draft.integrationType === IntegrationMap.company) {
        dispatch(setVendorConnecting(draft.providerName));
      }

      await createEsignatureIntegration({
        integrationType: draft.integrationType,
        vendor: draft.providerName,
      }).unwrap();

      if (draft.integrationType === IntegrationMap.company) {
        await executeReauthenticate();
      }
    } catch (error) {
      if (error instanceof Error) {
        toast(error, { autoDismiss: false });
      }
      dispatch(clearVendorConnecting());
    }
  };

  return {
    executeCreateIntegration,
    executeReauthenticate,
    isLoading: isCreateLoading || isAuthenticateLoading,
    isCompanyConnecting: isVendorConnecting,
  };
}

export interface UseESignatureIntegrationActionsParams {
  draft?: ESignatureIntegration;
}
